import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import SimpleBar from 'simplebar-react';
import type { Keyword, KeywordGroup } from '@brand24-original/llm-types';
import BrandIcon from '../../../../shared/components/brand-icon/BrandIcon';
import { useSetBrandLabel } from '../../../../shared/api/hooks/monitor/keyword/useSetBrandLabel';
import BrandNamesEditor from '../keyword-aliases/BrandNamesEditor';
import BrandDomainsEditor from '../keyword-domains/BrandDomainsEditor';
import type { OwnedKeywordDomain } from '../keyword-domains/KeywordDomainsCell';
// Globalne `.tabs` — importowane wprost, bo ten ekran nie musi ładować komponentu `Tabs`.
import '../../../../shared/components/tabs/Tabs.scss';
import './BrandDialog.scss';

export type BrandDialogTab = 'basic' | 'names' | 'domains';

const TABS: Array<{ id: BrandDialogTab; labelKey: string }> = [
  { id: 'basic', labelKey: 'brandTabBasic' },
  { id: 'names', labelKey: 'trackedNames' },
  { id: 'domains', labelKey: 'brandDomains' },
];

interface BrandDialogProps {
  projectId: number;
  visible: boolean;
  /** Marka w oknie — zostaje także podczas animacji zamykania. */
  group: KeywordGroup | null;
  tab: BrandDialogTab;
  onTabChange: (tab: BrandDialogTab) => void;
  onHide: () => void;
  /** Domena marki — favicon w nagłówku i przy nazwach. */
  domain?: string;
  /** Kolor marki z palety raportu — tło literki, gdy favicona nie ma. */
  color?: string;
  /** Domeny wszystkich nazw marki (główna + warianty). */
  domains: OwnedKeywordDomain[];
  /** Pozostałe marki projektu — cele dla „połącz z marką". */
  targets: Array<{ normalizedKeyword: string; name: string }>;
  isLocked: (keyword: Keyword) => boolean;
  status: { disabled: boolean; reason: string; onToggle: () => void };
  onError: () => void;
}

/**
 * Jedno okno edycji marki z zakładkami: podstawowe (nazwa wyświetlana, status), śledzone nazwy
 * i domeny. Komórki tabeli tylko pokazują chipy i otwierają to okno na właściwej zakładce.
 */
export const BrandDialog: React.FC<BrandDialogProps> = ({
  projectId,
  visible,
  group,
  tab,
  onTabChange,
  onHide,
  domain,
  color,
  domains,
  targets,
  isLocked,
  status,
  onError,
}) => {
  const { t } = useTranslation();
  const counts: Partial<Record<BrandDialogTab, number>> = group
    ? {
        names: group.aliases.length + 1,
        domains: domains.filter((entry) => !entry.isExcluded).length,
      }
    : {};

  return (
    <Dialog
      visible={visible && group !== null}
      onHide={onHide}
      className="dialog-box dialog-box--no-footer brand-dialog"
      draggable={false}
      dismissableMask
      header={
        group && (
          <span className="brand-dialog__title">
            <BrandIcon name={group.label ?? group.keyword} domain={domain} color={color} size="md" />
            <span className="brand-dialog__name">{group.label ?? group.keyword}</span>
            <span
              className={`brand-dialog__state${
                group.brandStatus === 'ACTIVE' ? '' : ' brand-dialog__state--off'
              }`}
            >
              {t(group.brandStatus === 'ACTIVE' ? 'keywordFilterActive' : 'keywordFilterInactive')}
            </span>
          </span>
        )
      }
    >
      {group && (
        <>
          <div className="tabs brand-dialog__tabs" role="tablist">
            {TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={item.id === tab}
                className={`tabs__item${item.id === tab ? ' tabs__item--active' : ''}`}
                onClick={() => onTabChange(item.id)}
              >
                {t(item.labelKey)}
                {counts[item.id] !== undefined && (
                  <span className="brand-dialog__count">{counts[item.id]}</span>
                )}
              </button>
            ))}
          </div>

          <SimpleBar className="brand-dialog__body" style={{ maxHeight: '60vh' }} autoHide={false}>
            {tab === 'basic' && (
              // `key` = marka: szkic nazwy nie może przejść z jednej marki na drugą.
              <BrandBasicSection
                key={group.canonical}
                projectId={projectId}
                group={group}
                status={status}
                onError={onError}
              />
            )}
            {tab === 'names' && <p className="brand-dialog__intro">{t('brandNamesIntro')}</p>}
            {tab === 'names' && (
              <BrandNamesEditor
                projectId={projectId}
                group={group}
                domain={domain}
                color={color}
                targets={targets}
                isLocked={isLocked}
                onError={onError}
              />
            )}
            {tab === 'domains' && <p className="brand-dialog__intro">{t('brandDomainsTooltip')}</p>}
            {tab === 'domains' && (
              <BrandDomainsEditor
                projectId={projectId}
                normalizedKeyword={group.normalizedKeyword}
                domains={domains}
              />
            )}
          </SimpleBar>
        </>
      )}
    </Dialog>
  );
};

interface BrandBasicSectionProps {
  projectId: number;
  group: KeywordGroup;
  status: BrandDialogProps['status'];
  onError: () => void;
}

/**
 * Nazwa wyświetlana marki (sama etykieta — grupowanie i statystyki trzymają się kanonika, więc
 * zmiana nazwy nie rusza żadnej liczby; puste pole przywraca nazwę domyślną) i status całej marki.
 */
const BrandBasicSection: React.FC<BrandBasicSectionProps> = ({ projectId, group, status, onError }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(group.label ?? '');
  const save = useSetBrandLabel(projectId, { onError });

  const commit = () => {
    const value = draft.trim();
    if (value === (group.label ?? '')) {
      return;
    }
    save.mutate({ projectId, canonical: group.canonical, label: value || null });
  };

  return (
    <div className="brand-dialog__section">
      <div className="brand-dialog__setting">
        <div className="brand-dialog__setting-head">
          <span className="brand-dialog__setting-title">{t('brandName')}</span>
          <span className="brand-dialog__setting-desc">{t('brandLabelEdit')}</span>
        </div>
        <InputText
          className="brand-dialog__input"
          value={draft}
          placeholder={group.keyword}
          disabled={save.isPending}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            }
          }}
          onBlur={commit}
        />
        <span className="brand-dialog__setting-desc">{t('brandLabelReset')}</span>
      </div>

      <div className="brand-dialog__setting brand-dialog__setting--inline">
        <div className="brand-dialog__setting-head">
          <span className="brand-dialog__setting-title">{t('status')}</span>
          <span className="brand-dialog__setting-desc">{status.reason || t('brandStatusDesc')}</span>
        </div>
        <InputSwitch
          className="input-switch--sm"
          checked={group.brandStatus === 'ACTIVE'}
          disabled={status.disabled}
          onChange={status.onToggle}
        />
      </div>
    </div>
  );
};

export default BrandDialog;
